/** How the master read ends for one row: the read fails, or a master comes back carrying this model. */
export type MasterCoverCaseMaster = { readonly read: 'fails' } | { readonly read: 'found'; readonly model: unknown };

/** One row of the contract: what the project points at, what the master read returns, and the verdict. */
export interface MasterCoverVerdictCase {
  readonly name: string;
  /** `false` — the flow is not «ученик собирает сам»; only the link gate judges the mode, the others are never asked then. */
  readonly studentAssembles: boolean;
  readonly masterAlbumId: string | null;
  readonly master: MasterCoverCaseMaster;
  /** `true` — the master cover counts as not filled, the student waits. */
  readonly expected: boolean;
}

const coverWith = (...texts: string[]): unknown => ({
  cover: {
    collages: [{ drawableObjects: texts.map((text, index) => ({ id: `t${index}`, type: 'text', text: { text } })) }],
  },
  spreads: [],
});

/**
 * The single table the three copies of the master cover verdict run: the public link gate
 * (api-gateway), the student read of the master (core) and the student editor gate (fotoclass-app).
 * Each side runs the rows its copy judges; a copy that changes the fail-closed table on its own fails
 * its suite here — which is why the table is exported rather than inlined into one spec.
 */
export const MASTER_COVER_VERDICT_CASES: readonly MasterCoverVerdictCase[] = [
  {
    name: 'a flow other than studentAssembles is never locked',
    studentAssembles: false,
    masterAlbumId: 'm-1',
    master: { read: 'found', model: coverWith('') },
    expected: false,
  },
  {
    name: 'no masterAlbumId pointer locks',
    studentAssembles: true,
    masterAlbumId: null,
    master: { read: 'found', model: coverWith('Гимназия 1', '11 А') },
    expected: true,
  },
  { name: 'a failed master read locks', studentAssembles: true, masterAlbumId: 'm-1', master: { read: 'fails' }, expected: true },
  {
    name: 'a null model (master not assembled) locks',
    studentAssembles: true,
    masterAlbumId: 'm-1',
    master: { read: 'found', model: null },
    expected: true,
  },
  {
    name: 'an absent model locks',
    studentAssembles: true,
    masterAlbumId: 'm-1',
    master: { read: 'found', model: undefined },
    expected: true,
  },
  {
    name: 'a v1 document the decoder throws on locks',
    studentAssembles: true,
    masterAlbumId: 'm-1',
    master: { read: 'found', model: { cover: { collages: [{ boxes: [] }] }, spreads: 'broken' } },
    expected: true,
  },
  {
    name: 'a string model passes as legacy without a cover and does not lock',
    studentAssembles: true,
    masterAlbumId: 'm-1',
    master: { read: 'found', model: 'abc' },
    expected: false,
  },
  {
    name: 'an empty object model does not lock',
    studentAssembles: true,
    masterAlbumId: 'm-1',
    master: { read: 'found', model: {} },
    expected: false,
  },
  {
    name: 'a book without a cover does not lock',
    studentAssembles: true,
    masterAlbumId: 'm-1',
    master: { read: 'found', model: { spreads: [] } },
    expected: false,
  },
  {
    name: 'a cover with an empty visible text locks',
    studentAssembles: true,
    masterAlbumId: 'm-1',
    master: { read: 'found', model: coverWith('Гимназия 1', '') },
    expected: true,
  },
  {
    name: 'a cover with a whitespace-only text locks',
    studentAssembles: true,
    masterAlbumId: 'm-1',
    master: { read: 'found', model: coverWith('Гимназия 1', ' ') },
    expected: true,
  },
  {
    name: 'a filled cover does not lock',
    studentAssembles: true,
    masterAlbumId: 'm-1',
    master: { read: 'found', model: coverWith('Гимназия 1', '11 А') },
    expected: false,
  },
];
